import React, { FormEvent, useCallback, useEffect, useState } from 'react';

import MediaPicker, { PickedFile } from '../MediaPicker/MediaPicker';
import PlatformBadge from '../PlatformBadge';
import apiClient from '../../services/api';
import { HashtagPreset, MaybePaginated, Post, SocialAccount } from '../../types/api';
import { VideoProject } from '../../types/editor';
import './CreatePostForm.css';

/**
 * Shared post composer. Used everywhere a post is created or edited:
 *  - the /create route (page wrapper)
 *  - the Calendar's inline-create modal (pre-scheduled for a date)
 *  - the editor's "Use in post" handoff (existing flow: edit pre-attached draft)
 *
 * Anything that should be consistent across those surfaces lives here.
 * Hosts decide chrome (page title, modal foot) and pass `onSaved`/
 * `onCancel` callbacks. The form itself owns all state, all field
 * markup, and the network round-trips.
 */
export interface CreatePostFormProps {
  /** Edit an existing post by id (string from URL params or numeric). */
  initialPostId?: string | number | null;
  /** Pre-fill the Schedule field. Calendar uses this. */
  initialScheduleAt?: Date | null;
  /** Notified after a successful save. Calendar/modal uses it to close. */
  onSaved?: (post: Post) => void;
  /** When provided, a Cancel button is rendered next to Submit. */
  onCancel?: () => void;
  /** Hide the form's own H2; the host page provides one. */
  embedded?: boolean;
}

// `<input type="datetime-local">` returns "YYYY-MM-DDTHH:MM" in the user's
// local timezone with no zone marker. Convert to a UTC ISO string for
// the API; the backend parses ISO 8601 + makes it timezone-aware.
function localDatetimeToISO(local: string): string | null {
  if (!local) return null;
  const d = new Date(local);
  if (isNaN(d.getTime())) return null;
  return d.toISOString();
}

function dateToLocalDatetime(d: Date): string {
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

export default function CreatePostForm({
  initialPostId,
  initialScheduleAt,
  onSaved,
  onCancel,
  embedded = false,
}: CreatePostFormProps) {
  const [accounts, setAccounts] = useState<SocialAccount[]>([]);
  const [selected, setSelected] = useState<Set<number>>(new Set());
  /* Instagram-only publish options. Collaborator accounts don't post
     their own feed copy — they get invited on the primary IG account's
     post (shared post + combined metrics once accepted). Story accounts
     additionally publish the media as a Story, which works for
     collaborators too since Stories are separate from the collab post. */
  const [igCollaborators, setIgCollaborators] = useState<Set<number>>(new Set());
  const [igStories, setIgStories] = useState<Set<number>>(new Set());
  const [description, setDescription] = useState('');
  const [files, setFiles] = useState<PickedFile[]>([]);
  const [existingPost, setExistingPost] = useState<Post | null>(null);
  const [scheduleFor, setScheduleFor] = useState(
    initialScheduleAt ? dateToLocalDatetime(initialScheduleAt) : '',
  );
  const [presets, setPresets] = useState<HashtagPreset[]>([]);
  const [videoLibrary, setVideoLibrary] = useState<VideoProject[]>([]);
  /* Project IDs the user picked from the video library — attached after
     the post saves. Kept separate from `files` because library items
     don't need re-upload; they already live on the server. */
  const [librarySelections, setLibrarySelections] = useState<Set<number>>(new Set());
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [info, setInfo] = useState<string | null>(null);

  const loadAccounts = useCallback(async (): Promise<void> => {
    setError(null);
    try {
      const data = await apiClient.get<MaybePaginated<SocialAccount>>('/accounts/social-accounts/');
      const rows = Array.isArray(data) ? data : data.results || [];
      setAccounts(rows.filter((a) => a.is_active));
    } catch (err) {
      setError((err as Error).message || 'Failed to load accounts');
    }
  }, []);

  const loadPresets = useCallback(async (): Promise<void> => {
    try {
      const data = await apiClient.get<MaybePaginated<HashtagPreset>>('/posts/hashtag-presets/');
      setPresets(Array.isArray(data) ? data : data.results || []);
    } catch (_) { /* presets are optional; quiet failure */ }
  }, []);

  const loadVideoLibrary = useCallback(async (): Promise<void> => {
    try {
      const data = await apiClient.get<MaybePaginated<VideoProject>>('/editor/projects/');
      const rows = Array.isArray(data) ? data : data.results || [];
      // A project shows up in the library if ANY aspect has a finished
      // render. At publish time the publisher picks the best aspect for
      // each platform from the attached PostMedia rows.
      setVideoLibrary(rows.filter((p) => (p.renders || []).some(
        (r) => r.status === 'done' && r.file_url,
      )));
    } catch (_) { /* library is optional; quiet failure */ }
  }, []);

  useEffect(() => {
    loadAccounts();
    loadPresets();
    loadVideoLibrary();
  }, [loadAccounts, loadPresets, loadVideoLibrary]);

  useEffect(() => {
    if (!initialPostId) {
      setExistingPost(null);
      return;
    }
    setError(null);
    apiClient.get<Post>(`/posts/posts/${initialPostId}/`).then((post) => {
      setExistingPost(post);
      setDescription(post.description || '');
      setSelected(new Set(post.social_accounts || []));
      const ig = post.platform_options?.instagram;
      setIgCollaborators(new Set(ig?.collaborators || []));
      setIgStories(new Set(ig?.stories || []));
      if (post.scheduled_at) {
        setScheduleFor(dateToLocalDatetime(new Date(post.scheduled_at)));
      }
    }).catch((err: Error) => {
      setError(err.message || 'Failed to load draft');
    });
  }, [initialPostId]);

  function toggleAccount(id: number): void {
    const next = new Set(selected);
    if (next.has(id)) {
      next.delete(id);
      // Deselected accounts can't hold IG roles.
      setIgCollaborators((prev) => { const s = new Set(prev); s.delete(id); return s; });
      setIgStories((prev) => { const s = new Set(prev); s.delete(id); return s; });
    } else {
      next.add(id);
    }
    setSelected(next);
  }

  function toggleInSet(setter: React.Dispatch<React.SetStateAction<Set<number>>>, id: number): void {
    setter((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  }

  function toggleLibrary(id: number): void {
    const next = new Set(librarySelections);
    if (next.has(id)) next.delete(id); else next.add(id);
    setLibrarySelections(next);
  }

  function applyPreset(preset: HashtagPreset): void {
    const trimmed = description.trimEnd();
    const sep = trimmed ? '\n\n' : '';
    setDescription(`${trimmed}${sep}${preset.hashtags.trim()}`);
  }

  async function handleSubmit(e: FormEvent): Promise<void> {
    e.preventDefault();
    setError(null);
    setInfo(null);
    if (selected.size === 0) {
      setError('Pick at least one connected account.');
      return;
    }
    const isoSchedule = localDatetimeToISO(scheduleFor);
    if (scheduleFor && !isoSchedule) {
      setError('Schedule time is invalid.');
      return;
    }
    if (isoSchedule && new Date(isoSchedule).getTime() <= Date.now()) {
      setError('Schedule time must be in the future.');
      return;
    }

    const selectedIgIds = accounts
      .filter((a) => a.platform === 'instagram' && selected.has(a.id))
      .map((a) => a.id);
    const collaborators = selectedIgIds.filter((id) => igCollaborators.has(id));
    const stories = selectedIgIds.filter((id) => igStories.has(id));
    if (collaborators.length > 3) {
      setError('Instagram allows at most 3 collaborators per post.');
      return;
    }
    if (collaborators.length > 0 && collaborators.length === selectedIgIds.length) {
      setError('At least one Instagram account must stay as the primary poster (not a collaborator).');
      return;
    }
    const platformOptions = (collaborators.length || stories.length)
      ? { instagram: { collaborators, stories } }
      : {};

    setSubmitting(true);
    try {
      let post: Post;
      if (existingPost) {
        post = await apiClient.put<Post>(`/posts/posts/${existingPost.id}/`, {
          description,
          status: existingPost.status || 'draft',
          social_accounts: Array.from(selected),
          platform_options: platformOptions,
        });
      } else {
        post = await apiClient.post<Post>('/posts/posts/', {
          description,
          status: 'draft',
          social_accounts: Array.from(selected),
          platform_options: platformOptions,
        });
      }

      // Upload freshly-picked files.
      const baseOrder = existingPost?.media?.length || 0;
      for (let i = 0; i < files.length; i += 1) {
        const f = files[i];
        const form = new FormData();
        form.append('file', f.file);
        form.append('media_type', f.kind);
        form.append('order', String(i + baseOrder));
        await apiClient.uploadFile(`/posts/posts/${post.id}/upload_media/`, form);
      }

      // Attach rendered videos picked from the library. Server copies bytes
      // so a future re-render of the project can't mutate this post.
      for (const projectId of librarySelections) {
        await apiClient.post(`/editor/projects/${projectId}/attach_to_post/`,
                              { post_id: post.id });
      }

      if (isoSchedule) {
        await apiClient.post(`/posts/posts/${post.id}/schedule/`,
                              { scheduled_for: isoSchedule });
      } else if (existingPost && existingPost.scheduled_at) {
        // User cleared the schedule field on an existing scheduled post.
        await apiClient.post(`/posts/posts/${post.id}/unschedule/`, {});
      }

      // Reset only when staying on the form (no host callback or page-style usage).
      if (!onSaved) {
        setDescription('');
        setSelected(new Set());
        setIgCollaborators(new Set());
        setIgStories(new Set());
        setFiles([]);
        setLibrarySelections(new Set());
        setScheduleFor('');
        setExistingPost(null);
      }
      const verb = isoSchedule ? 'scheduled' : (existingPost ? 'updated' : 'created');
      const when = isoSchedule ? ` for ${new Date(isoSchedule).toLocaleString()}` : '';
      const attachCount = files.length + librarySelections.size;
      const attach = attachCount ? ` with ${attachCount} new attachment(s)` : '';
      setInfo(`Draft #${post.id} ${verb}${when}${attach}.`);
      onSaved?.(post);
    } catch (err) {
      setError((err as Error).message || 'Failed to save draft');
    } finally {
      setSubmitting(false);
    }
  }

  const submitLabel = submitting
    ? 'Saving…'
    : scheduleFor
      ? (existingPost ? 'Update & reschedule' : 'Save & schedule')
      : (existingPost ? 'Update draft' : 'Save draft');

  return (
    <div className={embedded ? 'create-post-form embedded' : 'create-post-form'}>
      {!embedded && (
        <h2>{existingPost ? `Edit draft #${existingPost.id}` : 'Create Post'}</h2>
      )}
      {error && <div className="posts-error">{error}</div>}
      {info && <div className="posts-info">{info}</div>}

      <form className="post-form" onSubmit={handleSubmit}>
        {existingPost?.media?.length ? (
          <div className="post-form-section">
            <div className="section-label">Already attached</div>
            <ul className="media-tiles">
              {existingPost.media.map((m) => (
                <li key={m.id} className={`media-tile kind-${m.media_type}`}>
                  <div className="media-tile-thumb">
                    {m.media_type === 'image' && <img alt="attached" src={m.file} />}
                    {m.media_type === 'video' && <video src={m.file} muted preload="metadata" playsInline />}
                    {m.media_type === 'audio' && <div className="media-tile-audio">audio</div>}
                    <span className="media-tile-kind">{m.media_type}</span>
                  </div>
                </li>
              ))}
            </ul>
          </div>
        ) : null}

        <label>
          Description
          <textarea
            rows={5}
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            placeholder="What do you want to post?"
            required
          />
        </label>

        {presets.length > 0 && (
          <div className="post-form-section">
            <div className="section-label">Hashtag presets</div>
            <ul className="preset-row">
              {presets.map((p) => (
                <li key={p.id}>
                  <button type="button" className="secondary btn--sm" onClick={() => applyPreset(p)}>
                    + {p.name}
                  </button>
                </li>
              ))}
            </ul>
          </div>
        )}

        <div className="post-form-section">
          <div className="section-label">{existingPost ? 'Add more media' : 'Media'}</div>
          <MediaPicker files={files} onChange={setFiles} onNotice={setInfo} />
        </div>

        {videoLibrary.length > 0 && (
          <div className="post-form-section">
            <div className="section-label">From video library</div>
            <ul className="video-library">
              {videoLibrary.map((proj) => {
                const picked = librarySelections.has(proj.id);
                const doneRenders = (proj.renders || []).filter(
                  (r) => r.status === 'done' && r.file_url,
                );
                // Pick a thumbnail aspect — prefer the project's own
                // default; fall back to whatever exists. The post-time
                // publisher picks the best aspect per platform; this is
                // just the picker's preview.
                const thumb = doneRenders.find((r) => r.aspect === proj.aspect)
                          || doneRenders[0];
                return (
                  <li key={proj.id}>
                    <button
                      type="button"
                      className={picked ? 'video-library-tile picked' : 'video-library-tile'}
                      onClick={() => toggleLibrary(proj.id)}
                      aria-pressed={picked}
                    >
                      <div className="video-library-thumb">
                        {thumb?.file_url && (
                          <video src={thumb.file_url} muted preload="metadata" playsInline />
                        )}
                        {picked && <span className="video-library-check">✓</span>}
                      </div>
                      <div className="video-library-name">{proj.name || `Project #${proj.id}`}</div>
                      <div className="video-library-aspects">
                        {doneRenders.map((r) => (
                          <span key={r.aspect} className="video-library-aspect-badge">
                            {r.aspect}
                          </span>
                        ))}
                      </div>
                    </button>
                  </li>
                );
              })}
            </ul>
            {librarySelections.size > 0 && (
              <small className="muted">
                {librarySelections.size} project(s) will attach all their rendered aspects;
                each platform publishes the best fit.
              </small>
            )}
          </div>
        )}

        <div className="post-form-section">
          <div className="section-label">Post to</div>
          {accounts.length === 0 && (
            <p className="muted">
              No active accounts. Connect one on the Accounts tab first.
            </p>
          )}
          <ul className="account-picker">
            {accounts.map((a) => {
              const isIg = a.platform === 'instagram';
              const isPicked = selected.has(a.id);
              const igSelectedCount = accounts.filter(
                (x) => x.platform === 'instagram' && selected.has(x.id),
              ).length;
              const isCollab = igCollaborators.has(a.id);
              return (
                <li key={a.id}>
                  <label>
                    <input
                      type="checkbox"
                      checked={isPicked}
                      onChange={() => toggleAccount(a.id)}
                    />
                    <PlatformBadge platform={a.platform} />
                    <span>{a.account_name}</span>
                  </label>
                  {isIg && isPicked && (
                    <div className="ig-account-options">
                      {igSelectedCount > 1 && (
                        <label className="ig-account-option">
                          <input
                            type="checkbox"
                            checked={isCollab}
                            onChange={() => toggleInSet(setIgCollaborators, a.id)}
                          />
                          Collaborator
                        </label>
                      )}
                      <label className="ig-account-option">
                        <input
                          type="checkbox"
                          checked={igStories.has(a.id)}
                          onChange={() => toggleInSet(setIgStories, a.id)}
                        />
                        Story
                      </label>
                      {isCollab && (
                        <small className="muted">
                          invited on the primary account&apos;s post — no separate feed post
                          {igStories.has(a.id) ? '; Story still posts here' : ''}
                        </small>
                      )}
                    </div>
                  )}
                </li>
              );
            })}
          </ul>
          {igCollaborators.size > 0 && (
            <small className="muted">
              Collaborator invites are sent when the primary Instagram account publishes;
              each invitee accepts in-app, then the post (and its metrics) appear on both
              profiles. Max 3 collaborators.
            </small>
          )}
        </div>

        <div className="post-form-section">
          <div className="section-label">Schedule (optional)</div>
          <div className="schedule-row">
            <input
              type="datetime-local"
              className="schedule-input"
              value={scheduleFor}
              onChange={(e) => setScheduleFor(e.target.value)}
            />
            {scheduleFor && (
              <button type="button" className="ghost btn--sm" onClick={() => setScheduleFor('')}>
                Clear
              </button>
            )}
          </div>
          <small className="muted">
            Leave blank to save as a draft. Add a time to schedule for that moment (your timezone).
          </small>
        </div>

        <div className="create-post-form-actions">
          <button type="submit" disabled={submitting} className="primary">
            {submitLabel}
          </button>
          {onCancel && (
            <button type="button" className="ghost" onClick={onCancel} disabled={submitting}>
              Cancel
            </button>
          )}
        </div>
      </form>
    </div>
  );
}
